import { type LineItem } from "legacyComponents/LineItemContainer.types";
import moment from "moment";

import { isNullOrUndefined } from "utilities/object/object";

import { type ICostLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type IVendorProductsHandler } from "entity/vendor/common/vendor.utility.api.handler";

import type { default as VendorSendToCartResultExample } from "./json/SendToCart.partial.api.json";
import type { default as VendorSettingsExample } from "./json/VendorSettings.api.json";
import type { default as VendorStoreFinderExample } from "./json/VendorStoreFinder.api.json";

export enum VendorTypes {
    Unknown = 0,
    TheHomeDepot = 1,
}

export enum SendToVendorCartEntityTypes {
    SelectionChoices = 1,
    Estimates = 2,
    LeadProposals = 3,
    ChangeOrders = 4,
    PurchaseOrders = 5,
    Bills = 6,
}

type VendorSettingsType = typeof VendorSettingsExample;
type VendorStoreFinderType = typeof VendorStoreFinderExample;
type VendorDataType = VendorSettingsType["vendor"];
type StoreDataType =
    | VendorDataType["store"]
    | VendorStoreFinderType["currentStore"]
    | VendorStoreFinderType["stores"][number];
type CoordinateDataType = VendorDataType["store"]["location"];
type ConnectedUserDataType = VendorSettingsType["connectedUsers"][number];
type VendorSendToCartType = typeof VendorSendToCartResultExample;
type VendorLineItemType = VendorSendToCartType["validLineItems"][number];
type InvalidVendorLineItemType = VendorSendToCartType["invalidLineItems"][number];

export type VendibleLineItem = LineItem | ICostLineItem;

export class VendorSettingsEntity {
    constructor(data: VendorSettingsType) {
        this.isConnected = data.isConnected ?? false;
        if (data.vendor) {
            this.vendor = new Vendor(data.vendor);
        }
        if (data.connectedUsers) {
            this.connectedUsers = data.connectedUsers.map((c: any) => new ConnectedUser(c));
        }
        this.firstConnectedToVendorTourId = data.firstConnectedToVendorTourId;
    }
    isConnected: boolean;
    vendor?: Vendor;
    connectedUsers?: ConnectedUser[];
    firstConnectedToVendorTourId?: number;
}

export class Vendor {
    constructor(data: VendorDataType) {
        this.vendorType = data.vendorType;
        this.accountName = data.accountName;
        if (data.store) {
            this.store = new Store(data.store);
        }
        this.connectionDate = moment(data.connectionDate);
    }
    vendorType: VendorTypes;
    accountName: string;
    store?: Store;
    connectionDate: moment.Moment;
}

export class Store {
    constructor(data: StoreDataType) {
        this.storeId = data.storeId;
        this.location = new Coordinate(data.location);
        this.name = data.name;
        this.street = data.street;
        this.city = data.city;
        this.state = data.state;
        this.zip = data.zip;
        if (data.distance) {
            this.distance = Number(data.distance);
        }
    }
    storeId: string;
    location: Coordinate;
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    distance?: number;
}

export class Coordinate {
    constructor(data: CoordinateDataType) {
        this.latitude = Number(data.latitude);
        this.longitude = Number(data.longitude);
    }
    latitude: number;
    longitude: number;
}

export class ConnectedUser {
    constructor(data: ConnectedUserDataType) {
        this.name = data.name;
        this.userId = Number(data.userId);
        this.vendorAccountUserId = data.vendorAccountUserId;
        this.connectionDate = moment(data.connectionDate);
    }
    name: string;
    userId: number;
    vendorAccountUserId: string;
    connectionDate: moment.Moment;
}

export interface IVendorLineItem {
    vendorProductId: number;
    quantity: number;
}

export class VendorLineItem implements IVendorLineItem {
    constructor(data: VendorLineItemType) {
        this.vendorProductId = data.vendorProductId;
        this.quantity = data.quantity;
    }

    vendorProductId: number;
    quantity: number;
}

export interface IExternalProductLink {
    label: string;
    value: string;
    helpText?: string;
}

export class InvalidVendorLineItem implements IVendorLineItem {
    constructor(data: InvalidVendorLineItemType) {
        this.vendorProductId = data.vendorProductId;
        this.quantity = data.quantity;
        this.invalidReason = data.invalidReason;
        this.externalProductLink = data.externalProductLink;
    }

    vendorProductId: number;
    quantity: number;
    invalidReason: string;
    externalProductLink: IExternalProductLink;
}

export interface ISendToCartRequest {
    entityId: number;
    entityType: SendToVendorCartEntityTypes;
    jobId: number | undefined;
    builderId: number;
    vendorType: VendorTypes;
    lineItems: IVendorLineItem[];
}

export class SendToCartResponse {
    constructor(data: VendorSendToCartType) {
        this.validLineItems = data.validLineItems?.map((li: any) => new VendorLineItem(li)) ?? [];
        this.invalidLineItems =
            data.invalidLineItems?.map((li: any) => new InvalidVendorLineItem(li)) ?? [];
        this.redirectUrl = data.redirectUrl;
    }

    validLineItems: VendorLineItem[];
    invalidLineItems: InvalidVendorLineItem[];
    redirectUrl?: string;
}

export class VendorProductInventoryResponse {
    constructor(data: any) {
        this.inventory = new Map(Object.entries(data.inventory));
    }
    inventory: Map<string, DetailedVendorProductInformation>;
}

export class DetailedVendorProductInformation {
    constructor(data: any) {
        this.vendorType = data.vendorType;
        this.productName = data.productName;
        if (!isNullOrUndefined(data.count)) {
            this.count = Number(data.count);
        }
        this.location = data.location;
        this.isDiscontinued = data.isDiscontinued;
        this.externalImageUrl = data.externalImageUrl;
        this.externalProductLink = data.externalProductLink;
    }

    vendorType: VendorTypes;
    productName: string;
    count?: number;
    location: string;
    isDiscontinued?: boolean;
    externalImageUrl: string;
    externalProductLink: IExternalProductLink;
}

export class CanSendProductsToCartResponse extends SendToCartResponse {
    constructor(data: any) {
        super(data);
        this.isQuantityRounded = data.isQuantityRounded;
        this.vendorPurchaseLinkCount = data.vendorPurchaseLinkCount;
    }

    isQuantityRounded: boolean;
    vendorPurchaseLinkCount: number;
}

export interface ICanSendToCartEntity {
    canSendToHomeDepotCart: boolean;
}

export interface IViewVendorInventoryProps {
    vendorProductsHandler?: IVendorProductsHandler;
}

export interface ISendToVendorCartProps {
    vendorProductsHandler?: IVendorProductsHandler;
}

export interface ISendToVendorCartState {
    isSendToCartVisible: boolean;
    vendorType: VendorTypes | undefined;
    cartLineItems: VendibleLineItem[] | undefined;
}
